'use client';

import { useMemo, useSyncExternalStore } from 'react';

import { useSearchParam } from 'react-use';

import { EMBED_CONTEXT } from '@/common/constants';
import { useConfigValue } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import type { TargetDomainEntry } from '@/lib/types/tenant';
import { isInIframe } from '@/lib/utils/dom';
import { urlMatchesDomains } from '@/lib/utils/domain';

export type RenderingContext = 'tenant-site' | 'our-site' | 'standalone';

const subscribeNoop = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

export function useRenderingContext(): RenderingContext {
  const parentUrl = useSearchParam('parentUrl');
  const contextParam = useSearchParam('context');
  const isReadOnly = useReadOnly();

  // The classification depends on browser-only signals (`isInIframe`, the
  // `parentUrl` search param). Returning `'standalone'` until the client
  // commit keeps the first client render consistent with SSR and avoids
  // a hydration mismatch on link attributes (target/rel).
  const isClient = useSyncExternalStore(subscribeNoop, getClientSnapshot, getServerSnapshot);

  const { targetDomains } =
    useConfigValue<{
      targetDomains?: readonly TargetDomainEntry[];
    }>('navigation.iframeNavigation') ?? {};

  const isWidgetOnTargetDomain = useMemo(() => {
    if (!parentUrl || !targetDomains?.length) return false;
    return urlMatchesDomains({ href: parentUrl, domains: targetDomains });
  }, [parentUrl, targetDomains]);

  return useMemo<RenderingContext>(() => {
    if (!isClient) return 'standalone';
    if (!isInIframe() || contextParam === EMBED_CONTEXT.INLINE || isReadOnly) return 'standalone';
    if (isWidgetOnTargetDomain) return 'tenant-site';
    return 'our-site';
  }, [isClient, contextParam, isReadOnly, isWidgetOnTargetDomain]);
}
